import type { CourseAccreditation } from "@brookbush/types";

import { ArrowUpRightIcon } from "lucide-react";
import Image from "next/image";
import Link from "next/link";

import SectionContainer from "~/components/SectionContainer";
import TextBlock from "~/components/TextBlock";
import { CPT_PROGRAMS_PAGE } from "~/lib/cpt-programs-page";
import NccaAccreditedProgramMark from "~/pages/exam-portal/components/NccaAccreditedProgramMark";

interface LandingPageAccreditationsProps {
	accreditations: CourseAccreditation[];
	/** Prepend the NCCA CPT accreditation entry (mark + empty certificate slot). */
	showNccaCptEntry?: boolean;
}

const LandingPageAccreditations = ({
	accreditations,
	showNccaCptEntry = false,
}: LandingPageAccreditationsProps) => {
	if (!accreditations?.length && !showNccaCptEntry) return null;

	const remainingSlots = 4;

	return (
		<SectionContainer className="border bg-gradient-to-br from-sky-900/50 to-slate-900 py-12 text-center">
			<h2 className="font-bold text-2xl text-white sm:text-4xl lg:text-4xl">
				Accreditations and Approvals
			</h2>
			<p className="mx-auto mt-3 max-w-4xl text-lg text-white lg:text-xl">
				Brookbush Institute is accredited or approved by the following
				organizations.
			</p>
			<div className="mx-auto my-16 grid max-w-5xl grid-cols-1 gap-4 sm:gap-12">
				{showNccaCptEntry && (
					<div className="flex flex-col items-center gap-4 rounded-2xl border bg-white px-5 py-4 transition-all sm:flex-row sm:items-start sm:gap-6 dark:bg-slate-700">
						<div className="flex shrink-0 flex-col items-center gap-3">
							<NccaAccreditedProgramMark size="lg" />
							{/* Certificate of Accreditation slot — leave empty until issued */}
							<figure className="flex h-28 w-40 items-center justify-center rounded-lg border border-slate-300 border-dashed bg-slate-50 px-2 text-center text-slate-400 text-xs dark:border-slate-500 dark:bg-slate-800 dark:text-slate-500">
								<figcaption className="sr-only">
									{CPT_PROGRAMS_PAGE.nccaAccreditation.certificateAlt}
								</figcaption>
								Certificate slot
							</figure>
						</div>

						<div className="flex w-full flex-col items-start gap-2 text-left">
							<h3 className="font-semibold text-xl sm:text-2xl">
								{CPT_PROGRAMS_PAGE.nccaAccreditation.title}
							</h3>
							<p className="text-base dark:text-slate-100">
								{CPT_PROGRAMS_PAGE.nccaAccreditation.body}
							</p>
							<Link
								href="/exam-portal"
								className="mt-1 font-medium text-sky-700 hover:underline dark:text-sky-300"
							>
								Register for the exam →
							</Link>
						</div>
					</div>
				)}

				{accreditations.slice(0, remainingSlots).map((accreditation) => (
					<div
						key={accreditation._id}
						className="flex flex-col items-center gap-4 rounded-2xl border bg-white px-5 py-4 transition-all sm:flex-row sm:gap-6 dark:bg-slate-700"
					>
						<Link
							prefetch={false}
							className="relative h-48 w-32 shrink-0 overflow-hidden rounded-xl hover:brightness-95 sm:h-40 sm:w-56"
							href={`/about#accreditations`}
						>
							<Image
								src={
									accreditation.thumbnail?.url ||
									"/logos/brookbush-institute-logo-icon.png"
								}
								alt={accreditation.title}
								fill
								className={"object-contain"}
							/>
						</Link>

						<div className="flex w-full justify-between gap-2">
							<div className="group flex flex-col items-start gap-2">
								<h2 className="text-left font-semibold text-xl sm:text-2xl">
									{accreditation.title}
								</h2>{" "}
								<TextBlock
									className="!text-left line-clamp-5 dark:text-slate-800"
									value={accreditation.body}
								/>
							</div>
						</div>
					</div>
				))}
			</div>

			<Link
				prefetch={false}
				target="_blank"
				href="/about#accreditations"
				className="group col-span-full mx-auto flex max-w-max items-center gap-1 rounded-full bg-white px-4 py-1.5 font-medium text-lg text-slate-800 hover:brightness-95 sm:gap-2 sm:px-5 sm:py-2 sm:text-xl dark:bg-slate-600 dark:text-white"
			>
				See all of our accreditations{" "}
				<ArrowUpRightIcon className="transition-transform group-hover:translate-x-1 group-hover:-translate-y-1" />
			</Link>
		</SectionContainer>
	);
};

export default LandingPageAccreditations;
